import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Folder, FolderInput, FolderMinus, FolderPlus, Home, Link2, MessageSquare, MoreHorizontal,
  PanelLeftClose, PanelLeftOpen, Pencil, Pin, PinOff, Plus, Search, Shapes, Trash2, X,
} from "lucide-react";
import AccountMenu from "@/components/vai/AccountMenu";
import useClosing from "@/lib/useClosing";

// Barre latérale de Vanta AI : nouvelle conversation, recherche, conversations épinglées,
// dossiers, récents, retour au site et menu du compte (AccountMenu). Une conversation
// s'ouvre au clic ; clic droit ou « … » : épingler, renommer, copier le lien, déplacer
// vers un dossier, supprimer (raccourcis P, R, C, M, D quand le menu est ouvert).
// Glisser une conversation sur un dossier l'y range.

// Menu flottant affiché par-dessus tout (portail), là où on a cliqué.
function FloatingMenu({ x, y, onClose, children, testId }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  // Reste dans l'écran (aussi quand le contenu change de taille, ex. sous-menu « Déplacer »).
  useLayoutEffect(() => {
    const r = ref.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(x, window.innerWidth - r.width - 8));
    const top = Math.max(8, Math.min(y, window.innerHeight - r.height - 8));
    if (left !== pos.left || top !== pos.top) setPos({ left, top });
  }, [x, y, pos.left, pos.top, children]);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    window.addEventListener("resize", onClose);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); window.removeEventListener("resize", onClose); };
  }, [onClose]);
  return createPortal(
    <div ref={ref} role="menu" style={pos} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }} data-testid={testId}
      className="vai-pop-down fixed z-[90] w-60 rounded-xl border border-white/10 bg-[#1a1a22] shadow-2xl shadow-black/60 p-1.5 text-sm text-white">
      {children}
    </div>,
    document.body,
  );
}

function MenuItem({ icon: Icon, label, hint, onClick, danger, chevron, testId }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left hover:bg-white/[0.07] ${danger ? "text-red-300" : "text-white/85"}`}>
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {hint && <span className="text-[11px] text-white/35">{hint}</span>}
      {chevron && <ChevronRight className="w-3.5 h-3.5 text-white/40" />}
    </button>
  );
}

const Divider = () => <div className="my-1 h-px bg-white/[0.07]" />;

// Menu d'une conversation : épingler, renommer, copier le lien, déplacer, supprimer.
function ChatMenu({ chat, at, folders, actions, onClose, onRename }) {
  const [view, setView] = useState("main");
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const run = (fn) => () => { onClose(); fn(); };
  const pin = run(() => actions.onStar(chat));
  const rename = run(onRename);
  const copy = run(() => actions.onCopyLink(chat));
  const remove = run(() => actions.onDelete(chat));
  useEffect(() => {
    if (view !== "main") return undefined;
    const keys = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || /input|textarea/i.test(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "p") pin(); else if (k === "r") rename(); else if (k === "c") copy(); else if (k === "d") remove(); else if (k === "m") setView("move"); else return;
      e.preventDefault();
    };
    document.addEventListener("keydown", keys);
    return () => document.removeEventListener("keydown", keys);
  });
  return (
    <FloatingMenu x={at.x} y={at.y} onClose={onClose} testId="vai-chat-menu">
      {view === "main" ? (
        <>
          <MenuItem icon={chat.starred ? PinOff : Pin} label={chat.starred ? "Désépingler" : "Épingler"} hint="P" onClick={pin} testId="vai-menu-pin" />
          <MenuItem icon={Pencil} label="Renommer" hint="R" onClick={rename} testId="vai-menu-rename" />
          <MenuItem icon={Link2} label="Copier le lien" hint="C" onClick={copy} testId="vai-menu-copy" />
          <Divider />
          <MenuItem icon={FolderInput} label="Déplacer vers un dossier" chevron onClick={() => setView("move")} testId="vai-menu-move" />
          <Divider />
          <MenuItem icon={Trash2} label="Supprimer" hint="D" danger onClick={remove} testId="vai-menu-delete" />
        </>
      ) : (
        <>
          <button type="button" onClick={() => setView("main")} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-white/50 hover:text-white"><ChevronLeft className="w-3.5 h-3.5" />Déplacer vers un dossier</button>
          <div className="max-h-56 overflow-y-auto">
            {folders.map((f) => (
              <MenuItem key={f.id} icon={Folder} label={f.name} hint={chat.folder_id === f.id ? "✓" : ""} onClick={run(() => actions.onMove(chat, f.id))} testId={`vai-move-to-${f.id}`} />
            ))}
            {folders.length === 0 && <p className="px-2.5 py-1.5 text-xs text-white/40">Aucun dossier pour l'instant.</p>}
          </div>
          <Divider />
          {naming ? (
            <form className="px-1 py-1" onSubmit={async (e) => { e.preventDefault(); if (!name.trim()) return; onClose(); const f = await actions.onCreateFolder(name.trim()); if (f) actions.onMove(chat, f.id); }}>
              <input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={60} placeholder="Nom du dossier"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-2.5 py-1.5 text-sm outline-none" data-testid="vai-new-folder-inline" />
            </form>
          ) : <MenuItem icon={FolderPlus} label="Nouveau dossier…" onClick={() => setNaming(true)} testId="vai-move-new-folder" />}
          {chat.folder_id && <MenuItem icon={FolderMinus} label="Retirer du dossier" onClick={run(() => actions.onMove(chat, ""))} testId="vai-move-out" />}
        </>
      )}
    </FloatingMenu>
  );
}

function ChatItem({ chat, active, folders, actions }) {
  const [menuAt, setMenuAt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(chat.title);
  if (editing) {
    return (
      <form className="px-2" onSubmit={(e) => { e.preventDefault(); setEditing(false); if (title.trim() && title !== chat.title) actions.onRename(chat, title.trim()); }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus maxLength={120} onBlur={() => setEditing(false)}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-2.5 py-1.5 text-sm outline-none" data-testid="vai-rename-input" />
      </form>
    );
  }
  const openAtButton = (e) => { const r = e.currentTarget.getBoundingClientRect(); setMenuAt(menuAt ? null : { x: r.right - 240, y: r.bottom + 4 }); };
  return (
    <div draggable onDragStart={(e) => { e.dataTransfer.setData("text/vai-chat", chat.id); e.dataTransfer.effectAllowed = "move"; }}
      onContextMenu={(e) => { e.preventDefault(); setMenuAt({ x: e.clientX, y: e.clientY }); }}
      className={`vai-item group relative flex items-center rounded-lg transition-colors ${active || menuAt ? "bg-white/10" : "hover:bg-white/[0.05]"} ${chat.leaving ? "is-leaving" : ""}`}>
      <button type="button" onClick={() => actions.onOpen(chat.id)} className="flex-1 min-w-0 text-left px-2.5 py-1.5 text-sm text-white/80 truncate" title={chat.title} data-testid="vai-chat-item">{chat.title}</button>
      <button type="button" onClick={openAtButton} aria-label="Options de la conversation" aria-haspopup="menu" aria-expanded={!!menuAt}
        className={`p-1 mr-1 rounded-md text-white/50 hover:text-white hover:bg-white/10 ${menuAt ? "opacity-100" : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100"} focus:opacity-100`}><MoreHorizontal className="w-4 h-4" /></button>
      {menuAt && <ChatMenu chat={chat} at={menuAt} folders={folders} actions={actions} onClose={() => setMenuAt(null)} onRename={() => { setTitle(chat.title); setEditing(true); }} />}
    </div>
  );
}

// Une rubrique repliable (Épinglées, un dossier, Récents) ; on peut y déposer une conversation.
function Section({ title, icon: Icon, open, onToggle, onDropChat, extra, children, testId, empty }) {
  const [over, setOver] = useState(false);
  return (
    <div data-testid={testId}
      onDragOver={onDropChat ? (e) => { if (e.dataTransfer.types.includes("text/vai-chat")) { e.preventDefault(); setOver(true); } } : undefined}
      onDragLeave={onDropChat ? () => setOver(false) : undefined}
      onDrop={onDropChat ? (e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/vai-chat"); if (id) onDropChat(id); } : undefined}
      className={`rounded-lg transition-colors ${over ? "bg-blue-500/10 ring-1 ring-blue-400/40" : ""}`}>
      <div className="group/section flex items-center gap-1 px-1.5 pb-1">
        <button type="button" onClick={onToggle} className="flex items-center gap-1.5 min-w-0 flex-1 px-1 py-0.5 rounded text-[11px] font-medium text-white/40 hover:text-white/70">
          {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
          <span className="truncate">{title}</span>
          <ChevronDown className={`w-3 h-3 shrink-0 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        </button>
        {extra}
      </div>
      {open && <div className="vai-fade-quick">{children}{empty}</div>}
    </div>
  );
}

function FolderSection({ folder, chats, open, onToggle, currentId, folders, actions }) {
  const [menuAt, setMenuAt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  if (editing) {
    return (
      <form className="px-2 pb-1" onSubmit={(e) => { e.preventDefault(); setEditing(false); if (name.trim() && name !== folder.name) actions.onRenameFolder(folder, name.trim()); }}>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={60} onBlur={() => setEditing(false)}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-2.5 py-1 text-xs outline-none" />
      </form>
    );
  }
  return (
    <Section title={folder.name} icon={Folder} open={open} onToggle={onToggle} onDropChat={(id) => actions.onMoveId(id, folder.id)} testId={`vai-folder-${folder.id}`}
      empty={chats.length === 0 ? <p className="px-2.5 py-1 text-xs text-white/30">Dossier vide : glisse une conversation ici.</p> : null}
      extra={(
        <>
          <span className="text-[10px] text-white/30">{chats.length || ""}</span>
          <button type="button" aria-label={`Options du dossier ${folder.name}`} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenuAt({ x: r.right - 240, y: r.bottom + 4 }); }}
            className="p-0.5 rounded text-white/40 hover:text-white opacity-100 lg:opacity-0 lg:group-hover/section:opacity-100"><MoreHorizontal className="w-3.5 h-3.5" /></button>
          {menuAt && (
            <FloatingMenu x={menuAt.x} y={menuAt.y} onClose={() => setMenuAt(null)} testId="vai-folder-menu">
              <MenuItem icon={Pencil} label="Renommer le dossier" onClick={() => { setMenuAt(null); setName(folder.name); setEditing(true); }} />
              <MenuItem icon={Trash2} label="Supprimer le dossier" danger onClick={() => { setMenuAt(null); actions.onDeleteFolder(folder); }} testId="vai-folder-delete" />
            </FloatingMenu>
          )}
        </>
      )}>
      {chats.map((c) => <ChatItem key={c.id} chat={c} active={c.id === currentId} folders={folders} actions={actions} />)}
    </Section>
  );
}

// Rubriques repliées, gardées sur cet appareil.
function useFolds() {
  const [folds, setFolds] = useState(() => { try { return JSON.parse(localStorage.getItem("vai_folds") || "{}"); } catch { return {}; } });
  const toggle = (id) => setFolds((f) => {
    const next = { ...f, [id]: !f[id] };
    try { localStorage.setItem("vai_folds", JSON.stringify(next)); } catch { /* stockage indisponible */ }
    return next;
  });
  return [folds, toggle];
}

export default function Sidebar({ me, chats, folders = [], currentId, collapsed, onCollapse, mobileOpen, onMobileClose: closeNow, onNew, onOpen, onRename, onStar, onDelete,
  onCopyLink, onMove, onCreateFolder, onRenameFolder, onDeleteFolder, account, onArtifacts, artifactsActive }) {
  // Téléphone : le menu glisse en se fermant (sur ordinateur, rien à fermer).
  const [drawerClosing, closeDrawer] = useClosing(closeNow, 200);
  const onMobileClose = mobileOpen ? closeDrawer : closeNow;
  // Pendant la glissade seulement, le contenu qui dépasse est caché (sinon le menu du compte,
  // plus large que la barre repliée, serait coupé).
  const [sliding, setSliding] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return undefined; }
    setSliding(true);
    const t = setTimeout(() => setSliding(false), 320);
    return () => clearTimeout(t);
  }, [collapsed]);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const filtered = useMemo(() => (q.trim() ? chats.filter((c) => c.title.toLowerCase().includes(q.trim().toLowerCase())) : chats), [chats, q]);
  const [folds, toggleFold] = useFolds();
  const [creating, setCreating] = useState(false);
  const [folderName, setFolderName] = useState("");
  const folderIds = new Set(folders.map((f) => f.id));
  const starred = filtered.filter((c) => c.starred);
  const recents = filtered.filter((c) => !c.starred && !folderIds.has(c.folder_id));
  const searchingNow = !!q.trim();
  const isOpen = (id) => searchingNow || !folds[id];
  const actions = {
    onOpen: (id) => { onOpen(id); onMobileClose(); }, onRename, onStar, onDelete, onCopyLink, onMove, onCreateFolder, onRenameFolder, onDeleteFolder,
    onMoveId: (id, folderId) => { const c = chats.find((x) => x.id === id); if (c && c.folder_id !== folderId) onMove(c, folderId); },
  };
  const createFolder = async (e) => {
    e.preventDefault();
    const name = folderName.trim();
    setCreating(false);
    setFolderName("");
    if (name) await onCreateFolder(name);
  };

  const rail = (
    <div className="hidden lg:flex flex-col items-center w-14 h-full py-3 gap-2 border-r border-white/[0.06] bg-[#0b0b10]">
      <button type="button" onClick={() => onCollapse(false)} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Ouvrir la barre latérale"><PanelLeftOpen className="w-5 h-5" /></button>
      <button type="button" onClick={onNew} className="p-2 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white hover:brightness-110" aria-label="Nouvelle conversation"><Plus className="w-4 h-4" /></button>
      <button type="button" onClick={() => onCollapse(false)} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Conversations"><MessageSquare className="w-5 h-5" /></button>
      <button type="button" onClick={onArtifacts} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Artefacts" title="Artefacts"><Shapes className="w-5 h-5" /></button>
      <Link to="/" className="mt-auto p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Retour au site VantaPulse" title="Retour au site VantaPulse" data-testid="vai-home-rail"><Home className="w-5 h-5" /></Link>
      <AccountMenu me={me} collapsed {...account} />
    </div>
  );

  const panel = (
    <div className="flex flex-col w-72 h-full bg-[#0b0b10] border-r border-white/[0.06]" data-testid="vai-sidebar">
      <div className="flex items-center gap-2 px-3 h-14 shrink-0">
        <img src="/images/logo-navbar.png" alt="VantaPulse" className="w-7 h-7 rounded-full ring-1 ring-white/10" />
        <span className="font-semibold tracking-tight">Vanta AI</span>
        <button type="button" onClick={() => onCollapse(true)} className="ml-auto hidden lg:block p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" aria-label="Réduire la barre latérale"><PanelLeftClose className="w-5 h-5" /></button>
        <button type="button" onClick={onMobileClose} className="ml-auto lg:hidden p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" aria-label="Fermer"><X className="w-5 h-5" /></button>
      </div>
      <div className="px-2 space-y-0.5">
        <button type="button" onClick={() => { onNew(); onMobileClose(); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-white/[0.05]" data-testid="vai-new-chat">
          <span className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center"><Plus className="w-3.5 h-3.5 text-white" /></span>
          Nouvelle conversation
        </button>
        {searching ? (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10">
            <Search className="w-4 h-4 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Rechercher…" className="flex-1 bg-transparent text-sm outline-none" data-testid="vai-search" />
            <button type="button" onClick={() => { setQ(""); setSearching(false); }} aria-label="Fermer la recherche"><X className="w-4 h-4 text-white/40 hover:text-white" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => setSearching(true)} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-white/75 hover:bg-white/[0.05]"><Search className="w-4 h-4 mx-1" />Rechercher</button>
        )}
        <button type="button" onClick={() => { onArtifacts(); onMobileClose(); }} data-testid="vai-artifacts-link"
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm ${artifactsActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/[0.05]"}`}><Shapes className="w-4 h-4 mx-1" />Artefacts</button>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-2 pt-4 pb-2 space-y-4">
        {starred.length > 0 && (
          <Section title="Épinglées" icon={Pin} open={isOpen("pinned")} onToggle={() => toggleFold("pinned")} testId="vai-section-pinned">
            {starred.map((c) => <ChatItem key={c.id} chat={c} active={c.id === currentId} folders={folders} actions={actions} />)}
          </Section>
        )}
        {folders.map((f) => {
          const inside = filtered.filter((c) => !c.starred && c.folder_id === f.id);
          if (searchingNow && inside.length === 0) return null;
          return <FolderSection key={f.id} folder={f} chats={inside} open={isOpen(f.id)} onToggle={() => toggleFold(f.id)} currentId={currentId} folders={folders} actions={actions} />;
        })}
        {creating && (
          <form className="px-2" onSubmit={createFolder}>
            <div className="flex items-center gap-2 rounded-lg bg-white/[0.06] border border-white/15 px-2.5 py-1.5">
              <Folder className="w-3.5 h-3.5 text-white/50" />
              <input value={folderName} onChange={(e) => setFolderName(e.target.value)} autoFocus maxLength={60} placeholder="Nom du dossier" data-testid="vai-new-folder-name"
                onBlur={(e) => { if (!folderName.trim()) setCreating(false); else createFolder(e); }} onKeyDown={(e) => { if (e.key === "Escape") { setFolderName(""); setCreating(false); } }}
                className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
            </div>
          </form>
        )}
        <Section title="Récents" open={isOpen("recents")} onToggle={() => toggleFold("recents")} onDropChat={(id) => actions.onMoveId(id, "")} testId="vai-section-recents"
          extra={<button type="button" onClick={() => setCreating(true)} className="p-0.5 rounded text-white/40 hover:text-white" aria-label="Nouveau dossier" title="Nouveau dossier" data-testid="vai-new-folder"><FolderPlus className="w-3.5 h-3.5" /></button>}
          empty={recents.length === 0 ? <p className="px-2.5 py-1 text-xs text-white/30">{q ? "Aucun résultat" : "Aucune conversation"}</p> : null}>
          {recents.map((c) => <ChatItem key={c.id} chat={c} active={c.id === currentId} folders={folders} actions={actions} />)}
        </Section>
      </nav>
      <div className="p-2 border-t border-white/[0.06] space-y-1">
        <Link to="/" className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.05]" data-testid="vai-home">
          <ArrowLeft className="w-4 h-4 mx-1" />Retour au site VantaPulse
        </Link>
        <AccountMenu me={me} {...account} />
      </div>
    </div>
  );

  return (
    <>
      {/* Ordinateur : la barre s'élargit ou se replie en glissant. */}
      <div className={`hidden lg:block h-full shrink-0 ${sliding ? "overflow-hidden" : ""} transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none`}
        style={{ width: collapsed ? 56 : 288 }} data-testid="vai-sidebar-frame">
        <div key={collapsed ? "rail" : "panel"} className="vai-fade-quick h-full">{collapsed ? rail : panel}</div>
      </div>
      {mobileOpen && (
        <div className={`lg:hidden fixed inset-0 z-50 flex ${drawerClosing ? "is-closing" : ""}`}>
          <div className="vai-drawer h-full relative z-[1] shadow-2xl shadow-black/60">{panel}</div>
          <button type="button" className={`vai-overlay flex-1 bg-black/60 ${drawerClosing ? "is-closing" : ""}`} onClick={onMobileClose} aria-label="Fermer le menu" />
        </div>
      )}
    </>
  );
}
